import React, {
  Fragment,
  useEffect,
  useCallback,
  useRef,
  useState
} from 'react';
import { t } from 'ttag';

import { Dialog, Transition } from '@headlessui/react';
import { mergeClasses } from '@utils';

import SearchCategories from './Search.Categories';
import SearchProducts from './Search.Products';

export default function Search({ options: { locale } }) {
  const [isSearchVisible, toggleSearch] = useState(false);

  const [inputValue, setInputValue] = useState('');
  const [debouncedInputValue, setDebouncedInputValue] = useState('');

  const [searchResults, setSearchResults] = useState({});

  const [isLoading, setIsLoading] = useState(false);

  const inputRef = useRef(null);

  const handleSubmit = useCallback(
    event => {
      event.preventDefault();
      window.location.href = `/search/?q=${inputValue}`;
    },
    [inputValue]
  );

  const fetchSearchResults = useCallback(async searchQuery => {
    setIsLoading(true);
    const response = await fetch(
      `/api/search/?q=${searchQuery}&to=4&from=0&sort=default&suggestion=true`
    );

    if (!response.ok) {
      setIsLoading(false);
      return {};
    }

    setIsLoading(false);
    return response.json();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedInputValue(inputValue);
    }, 250);
    return () => clearTimeout(timeoutId);
  }, [inputValue, 150]);

  useEffect(() => {
    if (debouncedInputValue) {
      fetchSearchResults(debouncedInputValue).then(response => {
        setSearchResults(response);
      });
    } else {
      setSearchResults({});
    }
  }, [debouncedInputValue]);

  return (
    <>
      <button
        type="button"
        className="m-0 flex items-center gap-2 border-0 bg-transparent p-2 outline-none focus:outline-none"
        aria-label={t`Open Search`}
        onMouseUp={() => toggleSearch(prevState => !prevState)}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-6 w-6"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.3-4.3" />
        </svg>
        <span className="hidden w-48 border-0 border-b border-solid border-black py-1 text-left text-sm text-dark-gray xl:block">
          {t`Search`}
        </span>
      </button>
      <Transition show={isSearchVisible} as={Fragment}>
        <Dialog
          onClose={() => toggleSearch(false)}
          className="relative z-[6000]"
          initialFocus={inputRef}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-100"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-200"
            enterFrom="transform-gpu -translate-y-8 opacity-0"
            enterTo="transform-gpu translate-y-0 opacity-100"
            leave="ease-in-out duration-200"
            leaveFrom="transform-gpu translate-y-0 opacity-100"
            leaveTo="transform-gpu -translate-y-8 opacity-0"
          >
            <Dialog.Panel className="fixed inset-0 box-border max-h-screen overflow-y-auto bg-white/90 backdrop-blur-sm lg:inset-x-0 lg:top-0 lg:h-fit">
              <div className={mergeClasses('p-4 xl:p-8')}>
                <form onSubmit={handleSubmit}>
                  <div className="relative flex items-center justify-center">
                    <button
                      type="button"
                      onClick={() => toggleSearch(false)}
                      className="absolute right-8 top-1/2 m-0 hidden -translate-y-1/2 transform items-center border-0 bg-transparent p-2 text-black outline-none focus:outline-none lg:flex"
                      data-testid="close-search"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="h-6 w-6"
                      >
                        <path d="M18 6 6 18" />
                        <path d="m6 6 12 12" />
                      </svg>
                    </button>
                    <div className="relative w-full max-w-screen-sm border-0 border-b border-solid border-dark-gray">
                      <div className="pointer-events-none absolute top-1/2 -translate-y-1/2 transform">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="h-6 w-6"
                        >
                          <circle cx="11" cy="11" r="8" />
                          <path d="m21 21-4.3-4.3" />
                        </svg>
                      </div>
                      <div className="absolute right-0 top-1/2 flex -translate-y-1/2 transform">
                        {isLoading && (
                          <div className="flex items-center p-2 text-gray">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="h-6 w-6 animate-spin"
                            >
                              <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                            </svg>
                          </div>
                        )}
                        {inputValue && (
                          <button
                            type="button"
                            onClick={() => {
                              setInputValue('');
                              inputRef?.current?.focus();
                            }}
                            className="m-0 flex items-center border-0 bg-transparent p-2 text-black outline-none focus:outline-none"
                          >
                            {t`Clear`}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => toggleSearch(false)}
                          className="m-0 flex items-center border-0 bg-transparent p-2 text-black outline-none focus:outline-none lg:hidden"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="h-6 w-6"
                          >
                            <path d="M18 6 6 18" />
                            <path d="m6 6 12 12" />
                          </svg>
                        </button>
                      </div>
                      <label
                        htmlFor="search"
                        className="sr-only"
                      >{t`Search`}</label>
                      <input
                        id="search"
                        type="search"
                        ref={inputRef}
                        placeholder={t`What are you looking for?`}
                        value={inputValue}
                        onChange={e => setInputValue(e.target.value)}
                        className="w-full border-0 bg-transparent p-2 pl-8 text-base text-black placeholder:text-dark-gray focus:border-black focus:outline-none"
                      />
                    </div>
                  </div>
                </form>
                <SearchCategories
                  searchResults={searchResults}
                  searchQuery={debouncedInputValue}
                />
                <SearchProducts
                  searchResults={searchResults}
                  options={{ locale }}
                />
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </Dialog>
      </Transition>
    </>
  );
}
